// ======================================================================
// File: IndentApprovalDrawer.jsx
// Module: PPE → Procurement → Indent
//
// Purpose:
// - Approve submitted indent
// - Supports partial / full approval
// - Process Master–style right-side drawer
// - NO ERPSideDrawer usage
//
// ======================================================================

import React, { useEffect, useState } from "react";

import indentService from "../services/indentService";

export default function IndentApprovalDrawer({
  open,
  indent,
  onClose,
  onApproved,
}) {
  const [lines, setLines] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // --------------------------------------------------
  // LOAD LINES
  // --------------------------------------------------
  useEffect(() => {
    if (indent?.lines) {
      setLines(
        indent.lines.map((l) => ({
          line_id: l.id,
          material_name: l.material_name,
          required_qty: l.required_qty,
          approved_qty:
            l.approved_qty ?? l.required_qty,
        }))
      );
    }
  }, [indent]);

  if (!open) return null;

  // --------------------------------------------------
  // UPDATE QTY
  // --------------------------------------------------
  const updateQty = (index, value) => {
    const updated = [...lines];
    updated[index].approved_qty = Number(value);
    setLines(updated);
  };

  // --------------------------------------------------
  // APPROVE
  // --------------------------------------------------
  const handleApprove = async () => {
    setError(null);

    for (const l of lines) {
      if (
        l.approved_qty < 0 ||
        l.approved_qty > l.required_qty
      ) {
        setError(
          "Approved quantity must be between 0 and required quantity"
        );
        return;
      }
    }

    try {
      setSaving(true);

      await indentService.approveIndent(indent.id, {
        lines: lines.map((l) => ({
          line_id: l.line_id,
          approved_qty: l.approved_qty,
        })),
        remarks,
      });

      onApproved?.();
      onClose();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Indent approval failed"
      );
    } finally {
      setSaving(false);
    }
  };

  // --------------------------------------------------
  // UI
  // --------------------------------------------------
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        width: 700,
        height: "100vh",
        background: "#fff",
        boxShadow: "-4px 0 16px rgba(0,0,0,0.15)",
        zIndex: 2000,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ====================================================== */}
      {/* HEADER */}
      {/* ====================================================== */}
      <div
        style={{
          padding: 16,
          borderBottom: "1px solid #e5e7eb",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h6 style={{ margin: 0 }}>
          Approve Indent
        </h6>

        <button
          className="btn btn-sm btn-light"
          onClick={onClose}
        >
          ✕
        </button>
      </div>

      {/* ====================================================== */}
      {/* BODY */}
      {/* ====================================================== */}
      <div
        style={{
          padding: 16,
          flex: 1,
          overflowY: "auto",
        }}
      >
        {error && (
          <div className="alert alert-danger">
            {error}
          </div>
        )}

        {/* LINE TABLE */}
        <table className="table table-bordered">
          <thead className="table-light">
            <tr>
              <th>Material</th>
              <th>Required Qty</th>
              <th style={{ width: 160 }}>
                Approved Qty
              </th>
            </tr>
          </thead>

          <tbody>
            {lines.map((line, idx) => (
              <tr key={line.line_id}>
                <td>{line.material_name}</td>
                <td>{line.required_qty}</td>
                <td>
                  <input
                    type="number"
                    min="0"
                    max={line.required_qty}
                    className="form-control"
                    value={line.approved_qty}
                    onChange={(e) =>
                      updateQty(
                        idx,
                        e.target.value
                      )
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* REMARKS */}
        <div className="mt-3">
          <label className="form-label">
            Approval Remarks
          </label>
          <textarea
            rows={3}
            className="form-control"
            value={remarks}
            placeholder="Optional remarks"
            onChange={(e) =>
              setRemarks(e.target.value)
            }
          />
        </div>
      </div>

      {/* ====================================================== */}
      {/* FOOTER */}
      {/* ====================================================== */}
      <div
        style={{
          padding: 16,
          borderTop: "1px solid #e5e7eb",
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
        }}
      >
        <button
          className="btn btn-outline-secondary"
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </button>

        <button
          className="btn btn-success"
          disabled={saving}
          onClick={handleApprove}
        >
          {saving ? "Approving…" : "Approve"}
        </button>
      </div>
    </div>
  );
}
